import React from 'react';
import {
  Calendar,
  ClipboardList,
  Files,
  MessageSquare,
  Receipt,
  Stethoscope,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SecondaryNavItem {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  show: boolean;
}

interface PatientSecondaryNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  showAppointments: boolean;
  showTasks: boolean;
  showIntakes: boolean;
  showInvoices: boolean;
  showMessaging: boolean;
  showDocuments: boolean;
  showMdiCases: boolean;
}

/**
 * Lower-frequency surfaces docked under the patient header as a horizontal
 * pill strip. Visually subordinate to the primary tabs on the left rail —
 * matches the Athena "tabs under banner" pattern.
 */
export default function PatientSecondaryNav({
  activeTab,
  onTabChange,
  showAppointments,
  showTasks,
  showIntakes,
  showInvoices,
  showMessaging,
  showDocuments,
  showMdiCases,
}: PatientSecondaryNavProps) {
  const items: SecondaryNavItem[] = [
    {
      value: 'appointments',
      label: 'Appointments',
      icon: Calendar,
      show: showAppointments,
    },
    { value: 'tasks', label: 'Tasks', icon: ClipboardList, show: showTasks },
    {
      value: 'intakes',
      label: 'Intakes',
      icon: ClipboardList,
      show: showIntakes,
    },
    {
      value: 'documents',
      label: 'Documents',
      icon: Files,
      show: showDocuments,
    },
    {
      value: 'messaging',
      label: 'Messaging',
      icon: MessageSquare,
      show: showMessaging,
    },
    {
      value: 'invoices',
      label: 'Invoices',
      icon: Receipt,
      show: showInvoices,
    },
    {
      value: 'mdi-cases',
      label: 'MDI Cases',
      icon: Stethoscope,
      show: showMdiCases,
    },
  ];

  const visible = items.filter((i) => i.show);
  if (visible.length === 0) return null;

  return (
    <nav className='flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-white p-1.5'>
      {visible.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.value;
        return (
          <button
            key={item.value}
            type='button'
            onClick={() => onTabChange(item.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              isActive
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )}
          >
            <Icon className='h-3.5 w-3.5' />
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}
